import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackCTA, trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { DWI_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Before / During / After — the same editorial arc /wedding-photo-sharing uses,
 * seen from the other end.
 *
 * Deliberately the same three photographs in the same order as that page's arc,
 * because it is meant to read as the same device: there, the invitation is the
 * "Before" leading up to the gallery; here, the invitation IS the page and the
 * gallery is where the story continues. The card copy is written from this
 * page's side, and the section closes with the link across to that one.
 *
 * The claim boundary this section must respect: the arc is a description of the
 * COUPLE'S JOURNEY, not of linked records. A guest's RSVP and a guest's uploads
 * live in two systems with no key between them (GuestModel vs AttendeeModel), so
 * "During" and "After" may never imply that the people who replied are
 * automatically in the gallery, or that uploads are attributed to the guest list.
 */
const CARDS = [
  {
    key: "before",
    image: "/assets/images/invitation/invitation-1.jpg",
    width: 6558,
    height: 3659,
  },
  {
    key: "during",
    image: "/assets/images/moments/moment_2.jpg",
    width: 6558,
    height: 3659,
  },
  {
    key: "after",
    image: "/assets/images/moments/moment_3.jpg",
    width: 6558,
    height: 3659,
  },
];

const InvitationArc = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(DWI_SECTION.ARC, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-section-header">
          <h2 className="heading-136">
            {t("digitalWeddingInvitation.arc.heading")}
          </h2>
        </div>

        <div className="home-col-wrapper wps-cards">
          <div className="home-col-list">
            {CARDS.map(({ key, image, width, height }) => (
              <article key={key} className="home-col-item wps-card">
                <div className="col-im-link">
                  <img
                    src={image}
                    alt={t(`digitalWeddingInvitation.arc.cards.${key}.imageAlt`)}
                    loading="lazy"
                    decoding="async"
                    width={width}
                    height={height}
                    className="wps-card-im"
                  />
                </div>
                <p className="wps-eyebrow wps-card-label">
                  {t(`digitalWeddingInvitation.arc.cards.${key}.label`)}
                </p>
                <h3 className="made-22 wps-card-title">
                  {t(`digitalWeddingInvitation.arc.cards.${key}.title`)}
                </h3>
                <p className="body-15 wps-card-body">
                  {t(`digitalWeddingInvitation.arc.cards.${key}.body`)}
                </p>
              </article>
            ))}
          </div>
        </div>

        <p className="wps-center-action">
          <a
            className="wps-text-link"
            href={localize("/wedding-photo-sharing")}
            onClick={() =>
              trackCTA("digital_wedding_invitation_arc", {
                ...analyticsContext,
                ctaName: "wedding_photo_sharing",
              })
            }
          >
            {t("digitalWeddingInvitation.arc.link")}
          </a>
        </p>
      </div>
    </section>
  );
};

export default InvitationArc;
